import React, { useState } from 'react';

import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import { useScope } from '../../../context/PermissionContext';
import { cancelInitiationErrorCopy } from '../../../constants/admission';
import { formatDateIST } from '../../../utils/formatIST';

const REASON_MAX = 300;
const OTHER_REASON = 'Other';
// Owner-ruled pick-list (2026-10-13); the chosen sentence is sent as `reason`.
export const CANCEL_INITIATION_REASONS = [
  'Wrong discharge date entered',
  "Patient's condition changed — stay extended",
  'Initiated on the wrong patient',
  OTHER_REASON,
];
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';

/**
 * Cancel initiation (discharge-final FE-0, PO copy K8/K13). ConfirmationModal
 * family — never a native dialog. Reason is REQUIRED (CTA disabled while blank).
 * Refusals render inline and the modal stays open. One POST per click: `busy`
 * disables the CTA and `onCancel` is inert while in flight.
 *
 * @param admission  { planned_discharge_at (the confirmed date) }
 * @param onConfirm(reason) → { success, data?, error?, code? }  caller owns the POST
 * @param onDone(admission)  called with the AdmissionResponse after success (modal then closes)
 */
export default function CancelInitiationDialog({ admission, onConfirm, onDone, onClose }) {
  const { role } = useScope();
  const [choice, setChoice] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const reason = choice === OTHER_REASON ? typed.trim() : choice;
  const date = formatDateIST(admission && admission.planned_discharge_at).replace(/^0/, '');
  const invoice = admission && admission.invoice_number_printed;

  const run = async () => {
    if (busy || !reason) return;
    setBusy(true);
    setError(null);
    const res = await onConfirm(reason);
    setBusy(false);
    if (res.success) {
      if (typeof onDone === 'function') onDone(res.data);
      onClose();
      return;
    }
    setError(cancelInitiationErrorCopy(res.code, role) || res.error || 'The initiation could not be cancelled.');
  };

  return (
    <ConfirmationModal
      isOpen
      variant="danger"
      icon={null}
      title="Cancel initiation?"
      message={`The discharge date${date ? ` ${date}` : ''} will be removed and the bill goes back to an Interim Bill. Initiate again with the correct date \u2014 the same invoice number${invoice ? ` (${invoice})` : ''} will be used.`}
      ctaLabel={busy ? 'Cancelling…' : 'Cancel initiation'}
      cancelLabel="Keep as is"
      ctaDisabled={busy || !reason}
      onConfirm={run}
      onCancel={() => (busy ? null : onClose())}
    >
      <div className="text-left space-y-2 mb-3" data-testid="cancel-initiation-dialog">
        {error && (
          <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="cancel-initiation-error">
            {error}
          </div>
        )}
        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400" htmlFor="cancel-initiation-choice">Reason *</label>
        <select
          id="cancel-initiation-choice"
          className={inputCls}
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          data-testid="cancel-initiation-choice"
        >
          <option value="">Select a reason…</option>
          {CANCEL_INITIATION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {choice === OTHER_REASON && (
          <textarea
            className={inputCls}
            rows={2}
            maxLength={REASON_MAX}
            placeholder="Why is the initiation being cancelled?"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            data-testid="cancel-initiation-reason"
          />
        )}
      </div>
    </ConfirmationModal>
  );
}
